import React, { useEffect, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useHistory } from 'react-router-dom';

import { useTheme, makeStyles } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Button from '@material-ui/core/Button';

import CloseIcon from '@material-ui/icons/Close';

import Loading from 'app/components/loaders/Loading';

import useApi from 'app/context/useApi';
import useApp from 'app/context/useApp';

import ChargePaymentMethodForm from 'app/components/accounting/payments/ChargePaymentMethodForm';

const useDialogStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    notActive: {
        paddingTop: theme.spacing(6),
        paddingBottom: theme.spacing(6),
        textAlign: 'center'
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    checkAddress: {
        padding: theme.spacing(3)
    },
}));

export default function PurchaseTicketDialog(props) {

    const { open, onClose, onClosed, onCharge, user, club, urlPrefix, invitation } = props;

    const { app } = useApp();
    const api = useApi();
    const theme = useTheme();
    const history = useHistory();

    const [ step, setStep ] = useState(2);
    const [ paymentMethod, setPaymentMethod ] = useState('now');

    const [ loading, setLoading ] = useState(false);
    const [ stripePromise, setStripePromise ] = useState(null);
    const [ route, setRoute ] = useState(null);

    const classes = useDialogStyles();

    const setStripe = () => {
        if (club && Array.isArray(club.meta.payment_methods) && club.meta.payment_methods.includes('stripe') && club.meta.stripe_account_id) {
            const stripeOptions = {stripeAccount: club.meta.stripe_account_id};
            setStripePromise(loadStripe(process.env.MIX_STRIPE_KEY, stripeOptions));

        } else {
            setStripePromise(null);
        }
    }

    useEffect(() => {
        setStripe()
    }, [club]);

    const handleEnter = () => {
        if (invitation.ticket && invitation.ticket.pay_later) setStep(1);
        else setStep(2);
    }

    const handleNext = () => {
        let nextStep = step + 1;

        // Checks
        switch (nextStep) {
            case 2:
                if (paymentMethod === 'later') nextStep++;
                break;
        }

        setStep(nextStep);
    }

    const handleBack = () => {
        let nextStep = step - 1;

        // Checks
        if (nextStep < 1) nextStep = 1;
        if (event && (nextStep < 2)) nextStep = 2;
        if (nextStep === 3) nextStep = 2;
        if (nextStep === 4 && guestMethod == 'new') nextStep = 3;
        if (nextStep === 5 && tickets === 0) {
            if (guestMethod == 'new') nextStep = 3;
            else nextStep = 4;
        };

        setStep(nextStep);
    }

    useEffect(() => {
        const content = document.getElementById("invite-guest-dialog-content");

        if (content) {
            content.scrollTo(0, 0);
        }
    }, [step])

    const handlePaymentMethod = e => {
        setPaymentMethod(e.target.value);
    };

    const postPayByCheck = () => {
        setLoading(true);
        api.get(`/invitation/${invitation.id}/later`)
            .then(() => onCharge())
            .finally(() => setLoading(false));
    }

    const handleClosed = () => {
        setLoading(false);
        if (onClosed) onClosed();
        if (route) {
            history.push(route);
        }
    }

    const closeAndReroute = route => {
        setRoute(route);
        onClose();
    }

    return (
        <Dialog
            fullWidth
            maxWidth="sm"
            open={open}
            onClose={onClose}
            fullScreen={useMediaQuery(theme.breakpoints.down('sm'))}
            aria-labelledby="charge-payment-method"
            disableBackdropClick
            disableEscapeKeyDown
            TransitionProps={{
                onEnter: handleEnter,
                onExited: handleClosed
            }}
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="charge-payment-method">
                <span>Purchase Ticket</span>
                <IconButton
                    aria-label="Cancel"
                    className={classes.closeButton}
                    onClick={onClose}
                    >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {(step === 1) &&
                    <form>
                        <Typography variant="body1" color="textSecondary" component="legend" className={classes.marginBottom}>
                            Please select whether you would like to pay now or pay by check.
                        </Typography>
                        <RadioGroup
                            aria-label="paymentMethod"
                            name="paymentMethod"
                            value={paymentMethod}
                            onChange={handlePaymentMethod}
                            className={classes.marginBottom}
                            >
                            <FormControlLabel
                                value="now"
                                control={
                                    <Radio color="primary"/>
                                }
                                label="Pay Now"
                                disabled={!stripePromise}
                            />
                            <FormControlLabel
                                value="later"
                                control={
                                    <Radio color="primary"/>
                                }
                                label="Pay by Check"
                                disabled={!(invitation?.ticket && invitation.ticket.pay_later)}
                            />
                        </RadioGroup>
                        <Typography align="right">
                            <Button onClick={handleNext} color="primary" variant="contained">
                                Next
                            </Button>
                        </Typography>
                    </form>
                }
                {(step === 2) &&
                    <>
                        {stripePromise ? (
                            <Elements stripe={stripePromise}>
                                <DialogContentText gutterBottom>
                                    Select a payment method below.
                                </DialogContentText>
                                <ChargePaymentMethodForm
                                    onCharge={onCharge}
                                    user={user}
                                    invoice={invitation?.invoice}
                                    club={club}
                                    setLoading={setLoading}
                                    loading={loading}
                                    urlPrefix={urlPrefix}
                                    onClose={closeAndReroute}
                                />
                            </Elements>
                        ) : (
                            <Typography
                                className={classes.notActive}
                                variant="subtitle2"
                                color="error"
                                >
                                This organization has not enabled online payments. Please contact an organization administrator to find out how you can begin paying online.
                            </Typography>
                        )}
                    </>
                }
                {(step === 3) &&
                    <>
                        {app.club.meta.payment_methods.includes('check') ? (
                            <>
                                <Typography color="textSecondary" variant="subtitle2">
                                    To pay by check, please send payment to the following address:
                                </Typography>
                                <Typography
                                    variant="subtitle2"
                                    color="textSecondary"
                                    component="div"
                                    className={classes.checkAddress}
                                    >
                                    {app.club.meta.check_name}<br/>
                                    {app.club.meta.check_address_one} {app.club.meta.check_address_two ?? ''}<br/>
                                    {app.club.meta.check_city}, {app.club.meta.check_state}<br/>
                                    {app.club.meta.check_zip}
                                </Typography>
                            </>
                        ) : (
                            <Typography
                                className={classes.notActive}
                                variant="subtitle2"
                                color="error"
                                >
                                This organization has not added an address to send checks. Please contact an organization administrator to find out where you should pay by check.
                            </Typography>
                        )}
                        <Button onClick={postPayByCheck} color="primary" variant="contained" fullWidth>
                            Confirm Pay By Check
                        </Button>
                    </>
                }
            </DialogContent>
        </Dialog>
    )
}
